import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal } from '../components/ui';
import { Plus, RefreshCw, Download, Send, CheckCircle2, Trash2, FileBarChart, AlertTriangle } from 'lucide-react';

const TERMINER = [
  { v: '1', l: 'Termin 1 (jan–feb)' },
  { v: '2', l: 'Termin 2 (mar–apr)' },
  { v: '3', l: 'Termin 3 (mai–jun)' },
  { v: '4', l: 'Termin 4 (jul–aug)' },
  { v: '5', l: 'Termin 5 (sep–okt)' },
  { v: '6', l: 'Termin 6 (nov–des)' },
  { v: 'aarlig', l: 'Årlig (under 1 mill omsetning)' },
];

const STATUS_BADGE = {
  utkast:   'badge-warning',
  sendt:    'badge-info',
  godkjent: 'badge-success',
  avvist:   'badge-danger',
};

const MvaMeldingPage = () => {
  const [liste, setListe] = useState(null);
  const [valgt, setValgt] = useState(null);
  const [nyAapen, setNyAapen] = useState(false);
  const [sendAapen, setSendAapen] = useState(false);
  const [markerSendt, setMarkerSendt] = useState(false);
  const [skjema, setSkjema] = useState({ aar: new Date().getFullYear(), termin: '1', kommentar: '' });
  const [altinnRef, setAltinnRef] = useState('');

  const last = () => api.get('/mva-meldinger').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);

  const aapneDetalj = async (m) => {
    const r = await api.get(`/mva-meldinger/${m.id}`);
    setValgt(r.data);
  };

  const opprett = async () => {
    try {
      const r = await api.post('/mva-meldinger', skjema);
      setNyAapen(false);
      setSkjema({ aar: new Date().getFullYear(), termin: '1', kommentar: '' });
      await last();
      setValgt(r.data);
    } catch (e) {
      const err = e.response?.data;
      if (err?.id) {
        if (window.confirm(`${err.error}\n\nÅpne eksisterende?`)) {
          setNyAapen(false);
          const r = await api.get(`/mva-meldinger/${err.id}`);
          setValgt(r.data);
        }
      } else {
        alert(err?.error || 'Feilet.');
      }
    }
  };

  const oppdater = async () => {
    try {
      const r = await api.post(`/mva-meldinger/${valgt.id}/oppdater`);
      setValgt(r.data); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const lastNedXml = () => {
    window.open(`/api/mva-meldinger/${valgt.id}/xml`, '_blank');
  };

  const send = async () => {
    try {
      await api.post(`/mva-meldinger/${valgt.id}/send`);
      alert('Sendt til Altinn!');
      const r = await api.get(`/mva-meldinger/${valgt.id}`);
      setValgt(r.data); last(); setSendAapen(false);
    } catch (e) {
      const data = e.response?.data;
      if (data?.konfig_mangler) {
        const trenger = (data.trenger || []).join('\n  • ');
        alert(`${data.error}\n\nTrenger:\n  • ${trenger}\n\n${data.neste_steg || ''}`);
      } else {
        alert(data?.error || 'Innsending feilet.');
      }
    }
  };

  const lagreManuelt = async () => {
    try {
      await api.post(`/mva-meldinger/${valgt.id}/marker-sendt`, { altinn_ref: altinnRef });
      const r = await api.get(`/mva-meldinger/${valgt.id}`);
      setValgt(r.data); last(); setMarkerSendt(false); setAltinnRef('');
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (m) => {
    if (!window.confirm(`Slette MVA-melding for ${m.aar} termin ${m.termin}?`)) return;
    try {
      await api.delete(`/mva-meldinger/${m.id}`);
      if (valgt?.id === m.id) setValgt(null);
      last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader title="MVA-melding"
        subtitle="Beregn, generer XML og send til Skatteetaten via Altinn 3"
        actions={<button className="btn-primary" onClick={() => setNyAapen(true)}>
          <Plus size={14} /> Ny MVA-melding
        </button>} />

      <div className="grid" style={{ gridTemplateColumns: '320px 1fr', gap: 12 }}>
        {/* Liste */}
        <Card style={{ padding: 0, alignSelf: 'start' }}>
          {liste.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <FileBarChart size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
              <p className="fm-text-muted" style={{ margin: 0, fontSize: 12 }}>
                Ingen MVA-meldinger ennå.
              </p>
            </div>
          ) : liste.map((m) => (
            <div key={m.id}
                 onClick={() => aapneDetalj(m)}
                 style={{ padding: 10, borderBottom: '1px solid var(--theme-border)',
                          cursor: 'pointer',
                          background: valgt?.id === m.id ? 'var(--theme-bg-hover)' : 'transparent' }}>
              <div className="flex justify-between" style={{ alignItems: 'center' }}>
                <strong style={{ fontSize: 13 }}>{m.aar} · Termin {m.termin}</strong>
                <span className={`badge ${STATUS_BADGE[m.status] || 'badge-neutral'}`}>{m.status}</span>
              </div>
              <div className="fm-text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                {dato(m.fra_dato)} – {dato(m.til_dato)}
              </div>
              <div style={{ fontSize: 13, marginTop: 4,
                            color: m.skyldig >= 0 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                Skyldig: {kr(m.skyldig)}
              </div>
            </div>
          ))}
        </Card>

        {/* Detalj */}
        <div>
          {!valgt ? (
            <Card style={{ padding: 32, textAlign: 'center' }}>
              <FileBarChart size={48} className="fm-text-muted" style={{ marginBottom: 12 }} />
              <p className="fm-text-muted">Velg en MVA-melding fra listen, eller opprett en ny.</p>
            </Card>
          ) : (
            <>
              <Card style={{ marginBottom: 12, padding: 14 }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18 }}>
                      MVA-melding {valgt.aar} · Termin {valgt.termin}
                    </h2>
                    <div className="fm-text-muted" style={{ fontSize: 12 }}>
                      {dato(valgt.fra_dato)} – {dato(valgt.til_dato)}
                      {valgt.sendt_dato && ` · Sendt ${dato(valgt.sendt_dato.slice(0,10))}`}
                    </div>
                  </div>
                  <span className={`badge ${STATUS_BADGE[valgt.status]}`}>{valgt.status}</span>
                </div>

                <div className="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {valgt.status === 'utkast' && (
                    <button className="btn-ghost" onClick={oppdater}>
                      <RefreshCw size={14} /> Beregn på nytt
                    </button>
                  )}
                  <button className="btn-ghost" onClick={lastNedXml}>
                    <Download size={14} /> Last ned XML
                  </button>
                  {valgt.status === 'utkast' && (
                    <>
                      <button className="btn-primary" onClick={() => setSendAapen(true)}>
                        <Send size={14} /> Send til Altinn
                      </button>
                      <button className="btn-ghost" onClick={() => setMarkerSendt(true)}>
                        <CheckCircle2 size={14} /> Marker som sendt manuelt
                      </button>
                      <button className="btn-ghost" onClick={() => slett(valgt)}>
                        <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                      </button>
                    </>
                  )}
                </div>
              </Card>

              {/* Sammendrag */}
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                <Card style={{ padding: 14, textAlign: 'center' }}>
                  <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Utgående MVA</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{kr(valgt.sum_utgaende)}</div>
                </Card>
                <Card style={{ padding: 14, textAlign: 'center' }}>
                  <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Inngående MVA</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{kr(valgt.sum_inngaende)}</div>
                </Card>
                <Card style={{ padding: 14, textAlign: 'center',
                               background: valgt.skyldig >= 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)' }}>
                  <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                    {valgt.skyldig >= 0 ? 'Å betale' : 'Til gode'}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700,
                                color: valgt.skyldig >= 0 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                    {kr(Math.abs(valgt.skyldig))}
                  </div>
                </Card>
              </div>

              {/* Spesifikasjon */}
              <Card style={{ padding: 0, marginBottom: 12 }}>
                <div style={{ padding: 10, borderBottom: '1px solid var(--theme-border)' }}>
                  <h3 style={{ margin: 0, fontSize: 14 }}>Spesifikasjon pr MVA-kode</h3>
                </div>
                <table>
                  <thead><tr>
                    <th>Kode</th><th>Beskrivelse</th><th>Sats</th>
                    <th style={{ textAlign: 'right' }}>Grunnlag</th>
                    <th style={{ textAlign: 'right' }}>MVA</th>
                  </tr></thead>
                  <tbody>
                    {(valgt.spesifikasjon || []).length === 0 ? (
                      <tr><td colSpan={5} className="fm-text-muted" style={{ textAlign: 'center', padding: 16 }}>
                        Ingen MVA-poster i perioden.
                      </td></tr>
                    ) : valgt.spesifikasjon.map((r) => (
                      <tr key={r.kode}>
                        <td><code>{r.kode}</code></td>
                        <td>{r.beskrivelse}</td>
                        <td>{r.sats}%</td>
                        <td style={{ textAlign: 'right' }}>{kr(r.grunnlag)}</td>
                        <td style={{ textAlign: 'right',
                                     color: r.retning === 'utgaende' ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                          {kr(r.mva)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              {valgt.altinn_ref && (
                <Card style={{ padding: 10, marginBottom: 12, fontSize: 12 }}>
                  <strong>Altinn referanse:</strong> {valgt.altinn_ref}
                  {valgt.kvittering && (
                    <pre style={{ marginTop: 8, fontSize: 11, whiteSpace: 'pre-wrap' }}>{valgt.kvittering}</pre>
                  )}
                </Card>
              )}

              {valgt.xml && (
                <Card style={{ padding: 0 }}>
                  <details>
                    <summary style={{ padding: 10, cursor: 'pointer', fontSize: 13 }}>
                      Vis generert XML ({Math.round(valgt.xml.length / 1024)} kB)
                    </summary>
                    <pre style={{ padding: 10, margin: 0, fontSize: 10, maxHeight: 400, overflow: 'auto' }}>
                      {valgt.xml}
                    </pre>
                  </details>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Ny-modal */}
      <Modal open={nyAapen} onClose={() => setNyAapen(false)} title="Ny MVA-melding"
             footer={<>
               <button className="btn-ghost" onClick={() => setNyAapen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={opprett}>Opprett og beregn</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label>År</label>
            <input type="number" value={skjema.aar}
                   onChange={(e) => setSkjema({ ...skjema, aar: parseInt(e.target.value || '0', 10) })} />
          </div>
          <div>
            <label>Termin</label>
            <select value={skjema.termin} onChange={(e) => setSkjema({ ...skjema, termin: e.target.value })}>
              {TERMINER.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Kommentar (valgfritt)</label>
            <textarea rows={3} value={skjema.kommentar}
                      onChange={(e) => setSkjema({ ...skjema, kommentar: e.target.value })} />
          </div>
        </div>
      </Modal>

      {/* Send-modal */}
      <Modal open={sendAapen} onClose={() => setSendAapen(false)} title="Send MVA-melding til Altinn"
             footer={<>
               <button className="btn-ghost" onClick={() => setSendAapen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={send}>
                 <Send size={14} /> Send nå
               </button>
             </>}>
        <p style={{ fontSize: 13 }}>
          Meldingen sendes til Skatteetaten via Altinn 3 instansAPI.
        </p>
        <Card style={{ padding: 10, fontSize: 12, background: 'rgba(251,191,36,0.08)' }}>
          <AlertTriangle size={14} style={{ color: 'var(--theme-warning)' }} /> Sjekk at:
          <ul style={{ marginTop: 6, marginBottom: 0 }}>
            <li>Alle bilag i perioden er bokført og kontert riktig</li>
            <li>Perioden er låst (anbefalt før innsending)</li>
            <li>Organisasjonsnummer er satt under Innstillinger → Firma</li>
          </ul>
        </Card>
      </Modal>

      {/* Marker-sendt-modal */}
      <Modal open={markerSendt} onClose={() => setMarkerSendt(false)}
             title="Marker som sendt manuelt"
             footer={<>
               <button className="btn-ghost" onClick={() => setMarkerSendt(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagreManuelt}>Bekreft</button>
             </>}>
        <p style={{ fontSize: 13 }}>
          Hvis du har lastet opp XML manuelt på <a href="https://www.altinn.no" target="_blank" rel="noopener noreferrer">altinn.no</a>,
          kan du registrere kvitteringsreferansen her.
        </p>
        <label>Altinn-referanse (valgfritt)</label>
        <input value={altinnRef} onChange={(e) => setAltinnRef(e.target.value)}
               placeholder="f.eks. AR123456789" />
      </Modal>
    </>
  );
};

export default MvaMeldingPage;
